<script setup lang="ts">
import { useCommentaryParamsModal } from '@/stores/commentaryParamsModal'
import { storeToRefs } from 'pinia'
import {
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogOverlay,
  DialogPortal,
  DialogRoot,
  DialogTitle,
  DialogTrigger
} from 'radix-vue'
import { ref, onMounted } from 'vue'

const { isModalOpen } = storeToRefs(useCommentaryParamsModal())

const props = defineProps<{
  isOpen: boolean
}>()

// Default values for data
const defaultData = {
  game_hand: {
    game_type: '',
    game_mode_code: '',
    big_blind: null,
    ante: null,
    dealer_seat: null,
    sb_seat: null,
    bb_seat: null,
    game_type_code: '',
    game_group: null,
    date: null,
    roomid: '',
    straddle_seat: null,
    flop_pot: null,
    turn_pot: null,
    river_pot: null,
    final_pot: null,
    post_seats: [],
    currency: null
  },
  personality: '',
  language: '',
  output_length: ''
}

const data = ref({ ...defaultData })

onMounted(() => {
  const storedData = localStorage.getItem('commentaryParams')
  if (storedData) {
    try {
      data.value = JSON.parse(storedData)
    } catch (error) {
      console.error('Error parsing stored data:', error)
    }
  }
})

const handleClose = () => {
  isModalOpen.value = false
}

const handleSave = () => {
  localStorage.setItem('commentaryParams', JSON.stringify(data.value))
  handleClose()
}
</script>

<template>
  <DialogRoot v-model:open="props.isOpen">
    <DialogPortal>
      <DialogOverlay class="bg-blackA9 data-[state=open]:animate-overlayShow fixed inset-0 z-30" />
      <DialogContent
        class="fixed inset-0 bg-linear-to-br from-[#00cdac] to-[#02aab0] p-[25px] shadow-[hsl(206_22%_7%/35%)_0px_10px_38px_-10px,hsl(206_22%_7%/20%)_0px_10px_20px_-15px] focus:outline-hidden z-999 overflow-y-scroll"
      >
        <DialogTitle class="text-mauve12 m-0 text-[17px] font-semibold"
          >Update Commentary API Params</DialogTitle
        >
        <DialogDescription
          class="text-mauve11 mt-[10px] mb-5 text-[15px] leading-normal font-light"
        >
          Input the data fields below and click save to store in localStorage.
        </DialogDescription>

        <fieldset
          v-for="(value, key) in data.game_hand"
          :key="key"
          class="mb-[15px] flex items-center gap-5"
        >
          <label class="w-[138px] text-right text-[15px] font-medium" :for="key">{{ key }}</label>
          <input
            :id="key"
            v-model="data.game_hand[key]"
            class="shadow-green-500 focus:shadow-green-800 bg-white bg-opacity-80 inline-flex h-[35px] w-full flex-1 items-center justify-center rounded-[4px] px-[10px] text-[15px] leading-none shadow-[0_0_0_1px] outline-hidden focus:shadow-[0_0_0_2px]"
            :type="typeof value === 'number' ? 'number' : 'text'"
          />
        </fieldset>

        <fieldset class="mb-[15px] flex items-center gap-5">
          <label class="w-[138px] text-right text-[15px] font-medium" for="personality"
            >personality</label
          >
          <input
            id="personality"
            v-model="data.personality"
            class="shadow-green-500 focus:shadow-green-800 bg-white bg-opacity-80 inline-flex h-[35px] w-full flex-1 items-center justify-center rounded-[4px] px-[10px] text-[15px] leading-none shadow-[0_0_0_1px] outline-hidden focus:shadow-[0_0_0_2px]"
          />
        </fieldset>

        <fieldset class="mb-[15px] flex items-center gap-5">
          <label class="w-[138px] text-right text-[15px] font-medium" for="language"
            >language</label
          >
          <input
            id="language"
            v-model="data.language"
            class="shadow-green-500 focus:shadow-green-800 bg-white bg-opacity-80 inline-flex h-[35px] w-full flex-1 items-center justify-center rounded-[4px] px-[10px] text-[15px] leading-none shadow-[0_0_0_1px] outline-hidden focus:shadow-[0_0_0_2px]"
          />
        </fieldset>

        <fieldset class="mb-[15px] flex items-center gap-5">
          <label class="w-[138px] text-right text-[15px] font-medium" for="output_length"
            >output_length</label
          >
          <input
            id="output_length"
            v-model="data.output_length"
            class="shadow-green-500 focus:shadow-green-800 bg-white bg-opacity-80 inline-flex h-[35px] w-full flex-1 items-center justify-center rounded-[4px] px-[10px] text-[15px] leading-none shadow-[0_0_0_1px] outline-hidden focus:shadow-[0_0_0_2px]"
          />
        </fieldset>

        <div class="mt-[25px] flex justify-end gap-2">
          <DialogClose as-child>
            <button
              class="text-black hover:text-red-800 inline-flex h-[35px] items-center justify-center rounded-[4px] px-[15px] font-semibold leading-none focus:shadow-[0_0_0_2px] focus:outline-hidden"
              v-on:click="handleClose"
            >
              Close
            </button>
          </DialogClose>
          <DialogClose as-child>
            <button
              class="bg-green-500 text-black hover:bg-green-200 focus:shadow-green7 inline-flex h-[35px] items-center justify-center rounded-[4px] px-[15px] font-semibold leading-none focus:shadow-[0_0_0_2px] focus:outline-hidden"
              v-on:click="handleSave"
            >
              Save changes
            </button>
          </DialogClose>
        </div>
        <DialogClose
          class="text-grass11 hover:bg-green4 focus:shadow-green7 absolute top-[10px] right-[10px] inline-flex h-[25px] w-[25px] appearance-none items-center justify-center rounded-full focus:shadow-[0_0_0_2px] focus:outline-hidden"
          aria-label="Close"
        />
      </DialogContent>
    </DialogPortal>
  </DialogRoot>
</template>
